import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clapperboard, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useAuth } from "../context/AuthContext";
import { CallCard } from "../components/dashboard/CastingCalls";

const FILTERS = ["All", "United Kingdom", "United States", "Ireland"];

const Action = ({ c, user }) => user !== null && (
  <Link to={user ? "/dashboard?tab=casting" : "/register"} className="shrink-0 bg-primary text-primary-foreground px-5 py-3 font-bold uppercase text-xs tracking-wider hover:bg-[hsl(43,100%,60%)] transition-colors inline-flex items-center gap-2" data-testid={`public-call-action-${c.id}`}>
    {user ? "I'm available" : "Join to apply"} <ArrowRight size={14} />
  </Link>
);

export default function CastingCallsPage() {
  useSeo("Casting Calls for Extras | extras.tv", "Current casting calls for TV and film extras in the UK, USA and Ireland. Join extras.tv to put yourself forward.");
  const { user } = useAuth();
  const [calls, setCalls] = useState(null);
  const [country, setCountry] = useState("All");
  useEffect(() => {
    api.get("/public/casting-calls").then((r) => setCalls(r.data)).catch(() => setCalls([]));
  }, []);
  const shown = (calls || []).filter((c) => country === "All" || c.country === country);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="casting-calls-page">
      <p className="label-xs text-primary flex items-center gap-2"><Clapperboard size={12} /> Casting calls</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[0.95]">Productions looking for extras now</h1>
      <p className="text-white/60 mt-5 max-w-2xl">These are live requests from productions. Members with a live profile can tap "I'm available" and we'll send their profile to the production team. Members nearby are also emailed when a new call goes out.</p>
      <div className="flex flex-wrap gap-2 mt-10" data-testid="casting-calls-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setCountry(f)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${country === f ? "border-primary text-primary" : "border-white/15 text-white/60 hover:text-white"}`} data-testid={`casting-filter-${f.toLowerCase().replace(/ /g, "-")}`}>{f}</button>
        ))}
      </div>
      <div className="space-y-4 mt-8">
        {calls === null && <p className="label-xs">Loading…</p>}
        {calls !== null && !shown.length && (
          <div className="border border-dashed border-white/15 p-16 text-center" data-testid="public-calls-empty">
            <Clapperboard className="mx-auto text-white/20" size={48} />
            <p className="font-display text-2xl mt-6">No open casting calls right now</p>
            <p className="text-white/50 text-sm mt-2">{user ? "We'll email you when one comes up near you." : "Join extras.tv and we'll email you when one comes up near you."}</p>
          </div>
        )}
        {shown.map((c) => <CallCard key={c.id} c={c} action={<Action c={c} user={user} />} />)}
      </div>
      <div className="mt-16 border border-white/10 p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <p className="font-display text-2xl">Need extras for your production?</p>
        <Link to="/casting-request" className="border border-primary text-primary px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="casting-calls-request-link">Send a casting request</Link>
      </div>
    </div>
  );
}
